import { ActionIcon, Flex, Tooltip } from '@mantine/core';
import { IconDownload } from '@tabler/icons-react';
import { useMemo, useRef } from 'react';
import { DistributionTableSupportText } from '@dealstack/frontend/app/pages/Valuations/views/BreakpointSummary/DistributionRule/styled';
import { TextV2 } from '@dealstack/frontend/shared/components/TextV2/TextV2';
import {
	BreakpointSummaryPayload,
	CurrencyType,
	SecurityDetails,
} from '@dealstack/shared/lib/generated';
import { getCoreRowModel, getExpandedRowModel, useReactTable } from '@tanstack/react-table';
import { flexRender } from '@dealstack/frontend/shared/tables/flexRender';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import {
	TransValuations,
	useTranslationCommonUi,
	useTranslationValuations,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { DistributionRuleSupportButton } from '@dealstack/frontend/app/pages/Valuations/views/BreakpointSummary/DistributionRule/DistributionRuleSupportButton';
import { useDownloadInterestEventsExcel } from '@dealstack/frontend/app/pages/Valuations/views/BreakpointSummary/DistributionRule/useDownloadInterestEventsExcel';
import { ratchetAppliedDescription } from '@dealstack/frontend/app/pages/Valuations/views/BreakpointSummary/DistributionRule/ratchetAppliedDescription';
import { DateTime } from 'luxon';
import styled from 'styled-components';
import { SetRequired } from 'type-fest';
import { getDayCountConventionTranslation } from '@dealstack/frontend/shared/incomeRights';
import { VALUATION_ELEMENT_ID } from '@dealstack/shared/tests/testElementIds';
import { formatSummaryDecimalString } from '@dealstack/frontend/app/pages/Valuations/components/table/utils/formatting';

export interface DistributionRuleScope {
	dealId: string | undefined;
	valuationId: string | undefined;
	personId: string | undefined;
	mipId: string | undefined;
	resultId: string | undefined;
}

interface InterestDownloadRef {
	securityId: string;
	trancheKey: string;
}

interface FlatRow {
	index: string;
	description: string;
	isSubRow?: boolean;
	interestDownload?: InterestDownloadRef;
}

const TableContainer = styled.div`
	width: 100%;
	border-radius: 8px;
	overflow: hidden;
	border: 1px solid ${defaultTheme.v3.colors.gray[4]};
`;

const Table = styled.table`
	width: 100%;
	border-collapse: collapse;
`;

const TableRow = styled.tr<{ $isSubRow?: boolean }>`
	background-color: ${({ $isSubRow }) => ($isSubRow ? defaultTheme.v3.colors.gray[0] : 'white')};
	border-bottom: 1px solid ${defaultTheme.v3.colors.gray[4]};

	&:last-child {
		border-bottom: none;
	}
`;

const IndexCell = styled.td`
	background-color: ${defaultTheme.v3.colors.gray[2]};
	border-right: 1px solid ${defaultTheme.v3.colors.gray[4]};
	width: 40px;
	text-align: center;
	padding: 12px 8px;
`;

const DescriptionCell = styled.td<{ $isSubRow?: boolean }>`
	background-color: ${({ $isSubRow }) => ($isSubRow ? defaultTheme.v3.colors.gray[0] : 'white')};
	padding: 12px 16px;
	width: auto;
`;

const columns = [
	{
		header: 'Index',
		accessorKey: 'index',
		cell: ({ getValue }) => {
			return <TextV2 variant="sans/14/600">{getValue()}</TextV2>;
		},
		size: 40,
	},
	{
		header: 'Description',
		accessorKey: 'description',
		cell: ({ getValue }) => {
			return <TextV2 variant="sans/14/400">{getValue()}</TextV2>;
		},
	},
];

const ActionCell = styled.td<{ $isSubRow?: boolean }>`
	background-color: ${({ $isSubRow }) => ($isSubRow ? defaultTheme.v3.colors.gray[0] : 'white')};
	padding: 8px 12px;
	width: 44px;
	text-align: right;
`;

export const DistributionRuleTable = ({
	breakpointSummaryData,
	currency,
	scope,
}: {
	breakpointSummaryData: BreakpointSummaryPayload;
	currency: CurrencyType;
	scope?: DistributionRuleScope;
}) => {
	const tableElementRef = useRef<HTMLTableElement>(null);
	const tValuations = useTranslationValuations();
	const tCommonUI = useTranslationCommonUi();

	const descriptions = useMemo(() => {
		const desc: {
			description: string;
			subRows: {
				description: string;
				index: string;
				interestDownload?: InterestDownloadRef;
			}[];
		}[] = [];

		desc.push(
			{
				description: tValuations(
					'breakpoint_summary.distribution_rules_drawer.retained_cash_description',
					{
						context: breakpointSummaryData.retainedCashDescription.option,
					}
				),
				subRows: [],
			},
			{
				description: tValuations(
					'breakpoint_summary.distribution_rules_drawer.outgoing_payables_description',
					{
						context: breakpointSummaryData.outgoingPayablesDescription.option,
					}
				),
				subRows: [],
			}
		);

		breakpointSummaryData.tiers.forEach((tier) => {
			if (tier.description) {
				desc.push({
					description: tValuations(
						'breakpoint_summary.distribution_rules_drawer.tier_description',
						{
							securityDescriptionsByComponent: tier.description
								.map(({ component, securityNames }) => {
									return tValuations(
										'breakpoint_summary.distribution_rules_drawer.component_description',
										{
											context: component,
											securityNames,
										}
									);
								})
								.join('; and '),
						}
					),

					subRows: tier.securities
						.filter((s) => !!s.description)
						.map((s: SetRequired<SecurityDetails, 'description'>) => {
							const baseDescription = tValuations(
								'breakpoint_summary.distribution_rules_drawer.security_description',
								{
									context: s.description.compoundOption,
									securityName: s.securityName,
									rate: s.description.rate,
									issueDate: tValuations(
										'breakpoint_summary.distribution_rules_drawer.deemed_start_date',
										{
											count: s.description.dateOption,
											deemedStartDate: DateTime.fromISO(
												s.description.startDate,
												{ zone: 'utc' }
											).toFormat('dd/MM/yyyy'),
											postProcess: 'interval',
										}
									),
									compoundingPeriod: tValuations(
										'breakpoint_summary.distribution_rules_drawer.compounding_period_interval',
										{
											count: s.description.compoundingPeriod,
											postProcess: 'interval',
										}
									),
									dayCountConvention: getDayCountConventionTranslation(
										tCommonUI,
										s.description.dayCountConvention
									),
								}
							);
							const interestSuffix = s.totalInterest
								? `. ${tValuations('breakpoint_summary.distribution_rules_drawer.total_interest', { currency, amount: formatSummaryDecimalString(s.totalInterest) })}`
								: '';
							const canDownload =
								!!s.totalInterest && !!s.trancheKey && !!s.securityId;
							return {
								description: baseDescription + interestSuffix,
								index: '',
								interestDownload: canDownload
									? {
											securityId: s.securityId,
											trancheKey: s.trancheKey as string,
										}
									: undefined,
							};
						}),
				});
			}
		});

		if (breakpointSummaryData.residueTier.description) {
			desc.push({
				description: tValuations(
					'breakpoint_summary.distribution_rules_drawer.residue_description',
					{
						context: breakpointSummaryData.residueTier.description.option,
						securityNames: breakpointSummaryData.residueTier.description.securityNames,
					}
				),
				subRows: [],
			});
		}

		// Add warrant exercise details if present
		if (
			breakpointSummaryData.warrantsExercised &&
			breakpointSummaryData.warrantsExercised.length > 0
		) {
			const warrantSubRows: { description: string; index: string }[] = [];
			for (const warrant of breakpointSummaryData.warrantsExercised) {
				warrantSubRows.push({
					description: tValuations(
						'breakpoint_summary.distribution_rules_drawer.warrant_exercise_detail',
						{
							warrantName: warrant.exchangeSecurityName,
							unitsExercised: formatSummaryDecimalString(warrant.unitsExercised),
							underlyingUnitsReceived: formatSummaryDecimalString(
								warrant.underlyingUnitsReceived
							),
							totalExerciseCost: formatSummaryDecimalString(
								warrant.totalExerciseCost
							),
							averagePricePerUnit: formatSummaryDecimalString(
								warrant.averagePricePerUnit
							),
							currency,
						}
					),
					index: '',
				});
				// Add per-bucket sub-rows when multiple price buckets exist
				if (warrant.buckets && warrant.buckets.length > 1) {
					for (const bucket of warrant.buckets) {
						const dateStr = bucket.issueDates
							.map((d) => DateTime.fromISO(d, { zone: 'utc' }).toFormat('dd/MM/yyyy'))
							.join(', ');
						warrantSubRows.push({
							description: `${dateStr}: ${formatSummaryDecimalString(bucket.units)} units @ ${currency} ${formatSummaryDecimalString(bucket.pricePerUnit)}/unit`,
							index: '',
						});
					}
				}
			}
			desc.push({
				description: tValuations(
					'breakpoint_summary.distribution_rules_drawer.warrants_exercised_description'
				),
				subRows: warrantSubRows,
			});
		}

		// Add ratchet applied details if present
		if (
			breakpointSummaryData.ratchetsApplied &&
			breakpointSummaryData.ratchetsApplied.length > 0
		) {
			const ratchetSubRows: { description: string; index: string }[] = [];
			for (const ratchet of breakpointSummaryData.ratchetsApplied) {
				ratchetSubRows.push({
					description: ratchetAppliedDescription(tValuations, ratchet),
					index: '',
				});
			}
			desc.push({
				description: tValuations(
					'breakpoint_summary.distribution_rules_drawer.ratchets_applied_description',
					{ defaultValue: 'Ratchet entitlement adjustments' }
				),
				subRows: ratchetSubRows,
			});
		}

		return desc.map((d, index) => ({ ...d, index: (index + 1).toString() }));
	}, [breakpointSummaryData, tValuations, tCommonUI, currency]);

	// Flatten the data for Tanstack Table
	const flatData = useMemo<FlatRow[]>(() => {
		const flattened: FlatRow[] = [];

		descriptions.forEach((item) => {
			// Add main row
			flattened.push({
				index: item.index,
				description: item.description,
				isSubRow: false,
			});

			// Add sub-rows
			item.subRows.forEach((subRow) => {
				flattened.push({
					index: '',
					description: subRow.description,
					isSubRow: true,
					interestDownload: subRow.interestDownload,
				});
			});
		});

		return flattened;
	}, [descriptions]);

	const { download, isDownloading, isReady } = useDownloadInterestEventsExcel({
		dealId: scope?.dealId,
		valuationId: scope?.valuationId,
		personId: scope?.personId,
		mipId: scope?.mipId,
		resultId: scope?.resultId,
	});

	const table = useReactTable({
		data: flatData,
		columns,
		getCoreRowModel: getCoreRowModel(),
		getExpandedRowModel: getExpandedRowModel(),
		initialState: {
			expanded: true,
		},
	});

	return (
		<Flex direction="column" gap="1rem" justify="space-between" h="100%">
			<TableContainer>
				<Table
					data-testid={VALUATION_ELEMENT_ID.DISTRIBUTION_RULE_TABLE}
					ref={tableElementRef}
				>
					<tbody>
						{table.getRowModel().rows.map((row, rowIndex) => {
							const interestDownload = row.original.interestDownload;
							const buttonDisabled = !isReady || isDownloading;
							return (
								<TableRow
									key={row.id}
									data-testindex={rowIndex}
									$isSubRow={row.original.isSubRow}
								>
									<IndexCell>
										{flexRender(
											row.getAllCells()[0].column.columnDef.cell,
											row.getAllCells()[0].getContext()
										)}
									</IndexCell>
									<DescriptionCell
										data-testid={
											VALUATION_ELEMENT_ID.DISTRIBUTION_RULE_TABLE_DESCRIPTION_CELL
										}
										$isSubRow={row.original.isSubRow}
									>
										{flexRender(
											row.getAllCells()[1].column.columnDef.cell,
											row.getAllCells()[1].getContext()
										)}
									</DescriptionCell>
									<ActionCell $isSubRow={row.original.isSubRow}>
										{interestDownload && (
											<Tooltip
												label={tValuations(
													'breakpoint_summary.distribution_rules_drawer.download_interest_events_tooltip',
													{
														defaultValue:
															'Download interest events spreadsheet',
													}
												)}
											>
												<ActionIcon
													variant="subtle"
													size="sm"
													disabled={buttonDisabled}
													data-testid={
														VALUATION_ELEMENT_ID.DISTRIBUTION_RULE_TABLE_INTEREST_DOWNLOAD
													}
													onClick={() =>
														download({
															securityId: interestDownload.securityId,
															trancheKey: interestDownload.trancheKey,
														})
													}
												>
													<IconDownload size={16} />
												</ActionIcon>
											</Tooltip>
										)}
									</ActionCell>
								</TableRow>
							);
						})}
					</tbody>
				</Table>
			</TableContainer>
			<Flex direction="row" align="baseline" justify="center">
				<TransValuations
					i18nKey="breakpoint_summary.distribution_rules_drawer.support_text"
					defaults="<prompt>Something's wrong?</prompt><button>Contact support</button>"
					components={{
						prompt: <DistributionTableSupportText variant="sans/14/400" />,
						button: <DistributionRuleSupportButton />,
					}}
				/>
			</Flex>
		</Flex>
	);
};
